import { getContext, setContext } from "svelte"
import type { Readable } from "svelte/store"
import type { ContextSessionResponse, IdentityResponse } from "@kontextwerk/control-web-client"

import type { PermissionState } from "../permissions-model"

/** KB presentation view of the confirmed V2 context; the server stays authoritative. */
export type KehrblattControlAuthContext = {
  session: ContextSessionResponse | null
  /** Actual signed-in actor; support never replaces it with the represented member. */
  user: IdentityResponse | null
  permissions: PermissionState
  /** Member whose own-scoped data may be shown; the represented user during support. */
  currentUserId: string | null
  /** Membership whose own-scoped scheduling data may be shown; the represented one during support. */
  currentMembershipId: string | null
  /** Confirmed organization of the context; null for platform staff. */
  currentUserOrganizationId: string | null
}

const kehrblattControlAuthContextKey = Symbol("kehrblatt-control-auth-context")

export const setKehrblattControlAuthContext = (
  store: Readable<KehrblattControlAuthContext | null>,
) => {
  setContext(kehrblattControlAuthContextKey, store)

  return store
}

export const getKehrblattControlAuthContext = () =>
  getContext<Readable<KehrblattControlAuthContext | null>>(kehrblattControlAuthContextKey)

export const isSameConfirmedSessionBinding = (
  left: ContextSessionResponse,
  right: ContextSessionResponse,
): boolean => {
  if (left === right) return true

  if (
    left.session_id !== right.session_id ||
    left.context_revision !== right.context_revision ||
    left.product_issuer !== right.product_issuer ||
    left.password_change_required !== right.password_change_required ||
    left.user.id !== right.user.id ||
    left.context.kind !== right.context.kind
  ) {
    return false
  }

  if (left.context.kind === "organization" && right.context.kind === "organization") {
    return (
      left.context.organization_id === right.context.organization_id &&
      left.context.membership_id === right.context.membership_id &&
      left.context.membership_revision === right.context.membership_revision &&
      left.context.role_id === right.context.role_id &&
      left.context.role_revision === right.context.role_revision &&
      (left.context.delegation?.id ?? null) === (right.context.delegation?.id ?? null)
    )
  }

  if (left.context.kind === "platform" && right.context.kind === "platform") {
    return left.context.role === right.context.role
  }

  return false
}
